import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Kicker } from "./Kicker";
import { fadeIn } from "./fadeIn";

/**
 * Where we are now. The stack truth, then three cited numbers. Every figure
 * names its source and links to it; keep them to the published reports
 * (story research, MARKET-SIGNALS §1: A1, D4, D11).
 */
const STATS = [
  {
    figure: "29 min",
    text: "Average eCrime breakout time: from first access to the next machine.",
    source: "CrowdStrike 2026 Global Threat Report",
    href: "https://www.crowdstrike.com/en-us/press-releases/2026-crowdstrike-global-threat-report/",
  },
  {
    figure: "247 days",
    text: "Average time to identify and contain a breach.",
    source: "IBM Cost of a Data Breach Report 2026",
    href: "https://www.ibm.com/reports/data-breach",
  },
  {
    figure: "87%",
    text: "Of intrusions spanned several attack surfaces, with the signals spread across disconnected tools.",
    source: "Unit 42 2026 Global Incident Response Report",
    href: "https://www.paloaltonetworks.com/blog/2026/02/unit-42-global-ir-report",
  },
];

export function NowSection() {
  const reduce = useReducedMotion();
  return (
    <section className="bg-paper px-6 py-10 md:px-12 md:py-28" aria-labelledby="now-heading">
      <div className="mx-auto flex max-w-7xl flex-col gap-10 md:gap-14">
        <motion.div {...fadeIn(reduce)} className="flex max-w-3xl flex-col gap-4 md:gap-5">
          <Kicker>Where we are now</Kicker>
          <h2 id="now-heading" className="heading-lg text-ink text-balance">
            Attackers have AI doing the legwork. Breaches still take months to find and contain.
          </h2>
          <p className="hidden text-lg leading-relaxed text-ink-muted md:block">
            The evidence is usually there. It is spread across tools that don&apos;t talk to each
            other, or it was never logged at all.
          </p>
        </motion.div>

        <motion.p
          {...fadeIn(reduce, 0.05)}
          className="max-w-4xl border-l-2 border-primary-500 pl-5 text-xl font-medium leading-snug text-ink md:pl-7 md:text-2xl"
        >
          Your SIEM holds what was logged. Your EDR and AI SOC work what fired. Nobody owns the
          question in between: <span className="text-primary-600">were we hit?</span>
        </motion.p>

        <ul className="grid divide-y divide-rule border-y border-rule md:grid-cols-3 md:gap-6 md:divide-y-0 md:border-0">
          {STATS.map((s, i) => (
            <motion.li
              key={s.figure}
              {...fadeIn(reduce, 0.08 * i)}
              className="grid grid-cols-[6.5rem_1fr] gap-x-4 py-4 md:flex md:flex-col md:gap-2 md:rounded-2xl md:border md:border-rule md:bg-card md:p-7"
            >
              <span className="text-2xl font-semibold tracking-tight text-ink md:text-5xl">
                {s.figure}
              </span>
              <span className="flex flex-col gap-1 md:flex-1 md:gap-2">
                <span className="text-[15px] leading-relaxed text-ink-muted">{s.text}</span>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-ink-subtle underline decoration-rule underline-offset-4 hover:text-primary-600 md:mt-auto md:pt-2"
                >
                  {s.source}
                  <ArrowUpRight size={12} weight="bold" aria-hidden />
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              </span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
